import type { ToolCallMessagePart } from '@assistant-ui/react';
import { Loader } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import cx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { Children, isValidElement } from 'react';

import { ToolAwaitingInput } from './ToolAwaitingInput.tsx';
import { ToolContent } from './ToolContent.tsx';
import classes from './ToolDisplay.module.css';
import { ToolStatus } from './ToolStatus.tsx';
import { HudBox } from '#plugin-chat/components/hud/HudBox.tsx';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { usePartStatus } from '#plugin-chat/hooks/usePartStatus.ts';

// Slot components
interface RunningSlotProps {
  children: React.ReactNode;
  hideDuringInterrupt?: boolean;
}

function RunningSlot({ children, hideDuringInterrupt: _hideDuringInterrupt = true }: RunningSlotProps) {
  const { isRunning, isAwaitingInput } = usePartStatus();

  if (!isRunning || isAwaitingInput || !children) {
    return null;
  }

  return (
    <>
      <div className={classes.icon}>
        <Loader color="dark.1" size={16} />
      </div>
      <div className={classes.label}>
        <Shimmer color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)" variant="background">
          {children}
        </Shimmer>
      </div>
    </>
  );
}

interface CompleteSlotProps {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  color?: string;
}

function CompleteSlot({ children, icon: Icon = IconCheck, color = 'var(--mantine-color-green-4)' }: CompleteSlotProps) {
  const { isComplete, isCancelled } = usePartStatus();
  if (!isComplete || isCancelled) {
    return null;
  }
  return (
    <>
      <div className={classes.icon}>
        <Icon color={color} size={16} />
      </div>
      <div className={classes.label} style={{ '--status-color': color }}>
        {children}
      </div>
    </>
  );
}

interface ErrorSlotProps {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  color?: string;
}

function ErrorSlot({ children, icon: Icon = IconX, color = 'var(--mantine-color-red-4)' }: ErrorSlotProps) {
  const { isCancelled, isError } = usePartStatus();
  if (!isCancelled && !isError) {
    return null;
  }
  return (
    <>
      <div className={classes.icon}>
        <Icon color={color} size={16} />
      </div>
      <div className={classes.label}>{children}</div>
    </>
  );
}

interface ContentSlotProps {
  children: React.ReactNode;
  /** Enable shimmer effect during streaming (default: true) */
  shimmer?: boolean;
}

function ContentSlot({ children, shimmer = true }: ContentSlotProps) {
  const { isRunning, isError, isCancelled } = usePartStatus();
  // Bail out on error/cancelled — render components shouldn't paint potentially
  // empty or stale content over an error. The status row (DefaultStatus +
  // ErrorSlot, both driven by usePartStatus) already surfaces the failure.
  if (isError || isCancelled) {
    return null;
  }
  const isStreaming = shimmer && isRunning;

  return (
    <HudBox
      accentHue={240}
      bevel={10}
      cornerLength={60}
      corners={[]}
      cornerThickness={3}
      particleCount={2}
      primaryHue={250}
      strokeWidth={1}
      style={
        {
          '--hud-background-opacity': 0,
          '--hud-glow-opacity': 0,
        } as React.CSSProperties
      }
    >
      <div className={classes.content}>
        {isStreaming ? (
          <Shimmer
            color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)"
            duration={1000}
            repeatDelay={500}
            style={{ width: '100%' }}
            variant="background"
          >
            {children}
          </Shimmer>
        ) : (
          children
        )}
      </div>
    </HudBox>
  );
}

interface AwaitingInputSlotProps {
  children?: React.ReactNode;
}

function AwaitingInputSlot({ children }: AwaitingInputSlotProps) {
  return children;
}

export function ToolDisplay({
  children,
}: React.PropsWithChildren<Partial<ToolCallMessagePart<Record<string, unknown>>>>) {
  const status = usePartStatus();
  const childArray = Children.toArray(children);

  const statusChildren = childArray.filter(
    (child) => isValidElement(child) && child.type !== ContentSlot && child.type !== AwaitingInputSlot,
  );
  const contentChildren = childArray.filter((child) => isValidElement(child) && child.type === ContentSlot);
  const isToolStatusMessage = statusChildren.some((child) => isValidElement(child) && child.type !== CompleteSlot);
  const awaitingInputChildren = childArray.filter((child) => isValidElement(child) && child.type === AwaitingInputSlot);

  if (isToolStatusMessage && !awaitingInputChildren.length) {
    // return null;
  }

  return (
    <div
      className={cx(
        classes.toolDisplay,
        status.isCancelled && classes.cancelled,
        (status.isComplete || status.isCancelled) && classes.complete,
      )}
    >
      <AnimatePresence>
        <ToolStatus key="tool-status">{statusChildren}</ToolStatus>
        <ToolAwaitingInput key="tool-awaiting-input">{awaitingInputChildren}</ToolAwaitingInput>
        <ToolContent key="tool-content">{contentChildren}</ToolContent>
      </AnimatePresence>
    </div>
  );
}

// Attach slot components to ToolDisplay for compound component pattern
ToolDisplay.Running = RunningSlot;
ToolDisplay.Complete = CompleteSlot;
ToolDisplay.Error = ErrorSlot;
ToolDisplay.Content = ContentSlot;
ToolDisplay.AwaitingInput = AwaitingInputSlot;
